import { Icon, MiniLoader } from '@pwc/pwc-deals-ui';
import { CommentTarget, NavigationNodeId } from '@pwc/pwc-deals-web-common';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import { wrapWithCommentingWrapper } from 'features/project/ViewProject/ProjectOverview/utils/wrapWithCommentingWrapper';
import { useClickedOutsideOfRef } from '../../hooks/useClickedOutsideOfRef';
import ConnectedContentLookup from '../ConnectedContentLookup/ConnectedContentLookup';
import ConnectedContentRow from '../ConnectedContentRow/ConnectedContentRow';
import Separator from '../Separator';
import './ConditionalMultiSelectSearchWithLabel.scss';

const ConditionalMultiSelectSearchWithLabel = ({
  projectId,
  options,
  parentContentId,
  onSubmit,
  linkNarrative,
  search,
  setSearch,
  suggestionLimit = 10,
  isLoading,
  connectedContentType,
  searchField,
  modalCheckboxLabel = 'Don’t show this message again',
  connectedContent,
  error,
  isCommentingAllowedOnThisItem,
  pageContainerName,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { url } = useRouteMatch();
  const ref = useClickedOutsideOfRef(
    (wasClickedOutside) => wasClickedOutside && setDropdownOpen()
  );
  const { t } = useTranslation();

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);

  const contentTypes = useMemo(() => {
    if (url.includes('key-focus-areas')) {
      return ['analyses'];
    }
    return ['hypotheses', 'analyses'];
  }, [url]);

  const visibleOptions = useMemo(() => {
    const sortedByNearest = options.sort((a, b) => {
      return (
        Math.abs(Date.now() - new Date(a.dateModified)) -
        Math.abs(Date.now() - new Date(b.dateModified))
      );
    });

    return search.length === 0
      ? sortedByNearest.slice(0, suggestionLimit)
      : sortedByNearest;
  }, [options, search.length, suggestionLimit]);

  const handleCloseDropDown = () => {
    setDropdownOpen(false);
  };

  const obtainRef = useMemo(() => {
    if (!isModalOpen) {
      return ref;
    }

    return null;
  }, [isModalOpen, ref]);

  const connectedContentTexts = useMemo(() => {
    if (connectedContentType === 'analyses') {
      return {
        title:
          contentTypes.length === 1
            ? 'connectAnalyses'
            : 'connectRelatedAnalyses',
        description: 'selectOtherAnalysesToShowHowTheySupportEachOther',
        fieldKey:
          contentTypes.length === 1
            ? CommentTarget.ConnectedAnalyses
            : CommentTarget.ConnectedRelatedAnalyses,
        icon: (
          <Icon
            name="information"
            className="fill icon-yellow"
            width={16}
            data-testid="draft-icon"
          />
        ),
      };
    }
    return {
      title: t('connectKeyFocusArea', { KFATitle }),
      description: t('selectOnly1KfaTitle', { KFATitle }),
      fieldKey: CommentTarget.ConnectedKfa,
      icon: (
        <Icon
          name="information"
          className="fill icon-yellow"
          width={16}
          data-testid="draft-icon"
        />
      ),
    };
  }, [connectedContentType, contentTypes, KFATitle, t]);

  if (!error && isLoading === true) {
    return (
      <div
        className="connected-content-dropdown-container loading"
        data-testid="connected-content-dropdown-container"
      >
        <MiniLoader />
      </div>
    );
  }

  return (
    <div>
      {wrapWithCommentingWrapper(
        <div className="connected-content-info-connections">
          <Icon
            name="hyperlink"
            width={16}
            className="icon fill connect-content-icon"
          />
          <div className="">{t(connectedContentTexts.title)}</div>
        </div>,
        connectedContentTexts.fieldKey,
        isCommentingAllowedOnThisItem
      )}

      <div className="description">
        {connectedContentTexts.icon && connectedContentTexts.icon}
        {t(connectedContentTexts.description)}
      </div>
      <div className="input-field-container__separator">
        <Separator />
      </div>
      <div
        ref={obtainRef}
        className="input-field-container"
        data-testid="container-multi-select-search"
      >
        <ConnectedContentLookup
          searchField={searchField}
          setSearch={setSearch}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={() => {
            setDropdownOpen(true);
          }}
          pageContainerName={pageContainerName}
        />

        <div>
          {dropdownOpen && (
            <div
              className="multi-select-container"
              data-testid="connected-content-dropdown-container"
            >
              <div
                className="items-header"
                data-testid="items-header-suggestion-title"
              >
                {t('showingNumSuggestions', {
                  visibleOptionsLength: visibleOptions.length,
                })}
              </div>
              <ConnectedContentRow
                projectId={projectId}
                parentContentId={parentContentId}
                connectedContentType={connectedContentType}
                setCloseDropDown={handleCloseDropDown}
                options={visibleOptions}
                onSubmit={onSubmit}
                modalCheckboxLabel={modalCheckboxLabel}
                linkNarrative={linkNarrative}
                connectedContent={connectedContent}
                isModalOpen={(modalStatus) => {
                  setIsModalOpen(modalStatus);
                }}
                modalTitle={t('areYouSure')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConditionalMultiSelectSearchWithLabel;
